import * as moment from 'moment-timezone';

/**
 * Photo proof (GeoStamp, mobile 7.3) as the web shows it. Mirrors the phone's
 * src/utils/photoProof.js badge rules and Proof-sheet rows, from the attachment
 * JSON the API returns: `proof` (the record the phone built at the shutter),
 * `sha256` (the server's hash of the stored bytes), `stamped_url`, and the row's
 * created_at (kept as `received_at` on the Attachment model).
 */

export type PhotoProofBadge = 'verified' | 'unverified' | 'library';

export interface PhotoProofRow {
  label: string;
  value: string;
  /** 'ok' | 'bad' | undefined: colours the file-integrity value */
  tone?: 'ok' | 'bad';
}

export const DEFAULT_VERIFIED_ACCURACY_M = 10;
const OFFLINE_GAP_MINUTES = 10;
/** Beyond this offset the nearest station says nothing about where the photo is. */
const MAX_STATION_OFFSET_FT = 500;
const FACADE_NAMES: Record<string, string> = { N: 'North', S: 'South', E: 'East', W: 'West' };
const PROVIDER_LABELS: Record<string, string> = { phone: 'phone GPS', trimble: 'Trimble DA2' };
const POINTS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];

const finite = (v: any): number | null => {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
};

/** The proof object off an attachment row, whether the API sent it parsed or as JSON text. */
export function photoProofOf(attachment: any): any | null {
  const raw = attachment?.proof;
  if (!raw) {
    return null;
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }
  return typeof raw === 'object' ? raw : null;
}

/** The org's accuracy threshold for a Verified badge, in metres. */
export function verifiedThresholdM(appData: any): number {
  const v = finite(appData?.org_config?.photo_verified_accuracy_m);
  return v !== null && v > 0 ? v : DEFAULT_VERIFIED_ACCURACY_M;
}

/** Badge rules, as on the phone: library → library; mocked → never verified;
 *  verified needs precise permission and accuracy within the threshold. */
export function photoProofBadge(attachment: any, thresholdM = DEFAULT_VERIFIED_ACCURACY_M): PhotoProofBadge | null {
  const proof = photoProofOf(attachment);
  if (!proof) {
    return null;
  }
  if (proof.source === 'library') {
    return 'library';
  }
  if (proof.mocked === true) {
    return 'unverified';
  }
  const accuracy = finite(proof.accuracy_m);
  if (proof.permission === 'precise' && accuracy !== null && accuracy <= thresholdM) {
    return 'verified';
  }
  return 'unverified';
}

export function badgeLabel(badge: PhotoProofBadge | null, proof: any): string {
  switch (badge) {
    case 'verified':
      return 'Verified';
    case 'library':
      return 'Library photo';
    case 'unverified': {
      if (proof?.mocked === true) {
        return 'Mock location';
      }
      const accuracy = finite(proof?.accuracy_m);
      return accuracy === null ? 'Unverified' : `Unverified ±${Math.round(accuracy)} m`;
    }
    default:
      return '';
  }
}

export const compassPoint = (deg: number): string => POINTS[Math.round((((deg % 360) + 360) % 360) / 22.5) % 16];

const formatMoment = (m: moment.Moment | null): string =>
  m && m.isValid() ? `${m.format('D MMM YYYY')} · ${m.format('h:mm A z')}` : '';

/** Device capture time in the capture's own zone, e.g. "21 Oct 2026 · 7:40 AM CDT". */
export function formatCapturedAt(proof: any): string {
  if (!proof?.captured_at) {
    return '';
  }
  const m = proof.timezone ? moment.tz(proof.captured_at, proof.timezone) : moment.parseZone(proof.captured_at);
  return formatMoment(m);
}

/** A library pick's file date as written, e.g. "23 Oct 2026 · 9:12 AM (UTC-05:00)"; the zone is
 *  the file's own offset, shown only when the file had one. Mirrors the phone's formatFileTakenAt. */
export function formatFileTakenAt(proof: any): string {
  const value = proof?.file_taken_at;
  if (typeof value !== 'string' || !value) {
    return '';
  }
  const offset = /([+-]\d{2}:\d{2})$/.exec(value);
  const m = offset ? moment.parseZone(value, moment.ISO_8601, true) : moment(value, moment.ISO_8601, true);
  if (!m.isValid()) {
    return '';
  }
  return `${m.format('D MMM YYYY · h:mm A')}${offset ? ` (UTC${offset[1]})` : ''}`;
}

/** Whole minutes between the device capture and the server's receipt, or null. */
export function captureGapMinutes(proof: any, receivedAt: any): number | null {
  if (!proof?.captured_at || !receivedAt) {
    return null;
  }
  const captured = moment.parseZone(proof.captured_at);
  const received = moment(receivedAt);
  if (!captured.isValid() || !received.isValid()) {
    return null;
  }
  return Math.round(received.diff(captured, 'minutes', true));
}

/** The line beside the badge: where the photo came from. */
export function photoProofNote(proof: any): string {
  return proof?.source === 'library'
    ? 'Picked from the photo library'
    : 'Taken in Sitenotes · original untouched';
}

/** The rows a reviewer reads under a photo, in the phone's order; missing parts are left out. */
export function photoProofRows(attachment: any): PhotoProofRow[] {
  const proof = photoProofOf(attachment);
  if (!proof) {
    return [];
  }
  const rows: PhotoProofRow[] = [];
  const camera = proof.source === 'camera';
  const add = (label: string, value: string, tone?: 'ok' | 'bad') => {
    if (value) {
      rows.push(tone ? { label, value, tone } : { label, value });
    }
  };

  add('Captured', formatCapturedAt(proof));

  if (!camera) {
    // The phone reads the file's EXIF date at pick time (mobile 7.3); anyone can edit it.
    add('Taken (from the file)', formatFileTakenAt(proof));
  }

  const receivedAt = attachment?.received_at || attachment?.created_at;
  if (!camera && receivedAt) {
    // A library pick has no capture time of its own; when it reached Sitenotes is what is known.
    add('Added', formatMoment(moment(receivedAt)));
  }
  if (camera && receivedAt) {
    const m = proof.timezone ? moment.tz(receivedAt, proof.timezone) : moment(receivedAt);
    const gap = captureGapMinutes(proof, receivedAt);
    const offline = gap !== null && gap > OFFLINE_GAP_MINUTES;
    add('Received by server', `${formatMoment(m)}${offline ? ' · captured offline' : ''}`);
  }

  const lat = finite(proof.latitude);
  const lng = finite(proof.longitude);
  if (lat !== null && lng !== null) {
    const acc = finite(proof.accuracy_m);
    add(camera ? 'Position' : 'Position in the file', `${lat.toFixed(6)}, ${lng.toFixed(6)}${acc === null ? '' : ` ±${Math.round(acc)} m`}`);
  }

  const heading = finite(proof.heading_deg);
  const facade = proof.facade && FACADE_NAMES[proof.facade] ? `${FACADE_NAMES[proof.facade]} elevation` : '';
  if (heading !== null) {
    const whole = Math.round(heading) % 360;
    const ref = proof.heading_ref === 'T' ? 'true' : 'magnetic';
    add('Heading', `${String(whole).padStart(3, '0')}° ${ref}, ${compassPoint(whole)}${facade ? ` · ${facade}` : ''}`);
  } else if (facade) {
    add('Heading', facade);
  }

  const offsetFt = finite(proof.offset_ft);
  if (proof.station && (offsetFt === null || Math.abs(offsetFt) <= MAX_STATION_OFFSET_FT)) {
    const offset = offsetFt;
    const side = proof.offset_side === 'L' ? 'left' : proof.offset_side === 'R' ? 'right' : '';
    add('Station', `${proof.station}${offset !== null && side ? `, ${Math.round(offset)} ft ${side}` : ''}`);
  }

  if (typeof proof.address === 'string' && proof.address.trim()) {
    add('Address', proof.address.trim());
  }

  if (camera) {
    add('Taken by', `${proof.user_name || 'Sitenotes user'} · ${PROVIDER_LABELS[proof.provider] || PROVIDER_LABELS.phone}`);
    const serverHash = typeof attachment?.sha256 === 'string' ? attachment.sha256.toLowerCase() : '';
    const captureHash = typeof proof.sha256 === 'string' ? proof.sha256.toLowerCase() : '';
    if (serverHash && captureHash) {
      const match = serverHash === captureHash;
      add('File integrity', match ? 'Matches capture hash' : 'Does not match capture hash', match ? 'ok' : 'bad');
    } else {
      add('File integrity', 'Not checked');
    }
  }

  return rows;
}
